// RunHistory.jsx — Warehouse portal: every applied run (Add Inventory / Cycle Count / Stock Take) at a location.
// Reopen a run's review report (scans per user, before/after) and, as an admin, reverse it.
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPresserSession, pressingFetch, authenticatedFetch, useAuth } from './AuthComponent';
import { useStore } from './StoreContext';
import useConsoleLocations from './useConsoleLocations';
import { hasRole } from './constants/roles';
import { exportReviewCsv } from './invExport';

const TYPE_LABEL = { stock_take: 'Stock Take', cycle_count: 'Cycle Count', add_inventory: 'Add Inventory' };
const fmt = (t) => (t ? new Date(t).toLocaleString() : '—');

const S = {
    shell: { minHeight: '100vh', background: '#f3f4f6' },
    bar:   { background: '#1e3a5f', padding: 'calc(18px + env(safe-area-inset-top, 0px)) 20px 14px', color: '#fff' },
    card:  { background: '#fff', margin: '12px', borderRadius: 10, padding: 12, boxShadow: '0 1px 4px rgba(0,0,0,.07)' },
    btn:   { padding: '9px 16px', fontSize: 14, background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontWeight: 600, color: '#374151' },
    danger:{ padding: '10px 18px', fontSize: 14, background: '#b91c1c', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, color: '#fff' },
    th:    { textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid #e5e7eb', fontSize: 12, fontWeight: 700, color: '#374151' },
    td:    { padding: '6px 8px', borderBottom: '1px solid #f3f4f6', fontSize: 13, verticalAlign: 'top' },
};

export default function RunHistory({ consoleMode = false }) {
    const navigate        = useNavigate();
    const { user }        = useAuth();
    const { activeStore } = useStore();
    const consoleLocs     = useConsoleLocations(consoleMode);
    const [session]       = useState(() => consoleMode ? null : getPresserSession());
    const isAdmin         = consoleMode && hasRole(user, ['admin']);

    const assignments = useMemo(() => {
        if (!consoleMode) return session?.assignments || [];
        if (!Array.isArray(consoleLocs)) return [];
        return consoleLocs.map(l => ({ shopify_location_id: l.shopify_location_id, location_name: l.name, store_key: activeStore }));
    }, [consoleMode, session, consoleLocs, activeStore]);

    const [loc,     setLoc]     = useState(null);
    const [runs,    setRuns]    = useState(null);
    const [detail,  setDetail]  = useState(null);
    const [plan,    setPlan]    = useState(null);    // reverse-preview rows
    const [choices, setChoices] = useState({});
    const [busy,    setBusy]    = useState(false);
    const [msg,     setMsg]     = useState(null);

    const invFetch = useMemo(() => {
        if (consoleMode) return (path, opts = {}) => authenticatedFetch(path, opts);
        const pid = session?.presser_id;
        return (path, opts = {}) => pressingFetch(`${path}${path.includes('?') ? '&' : '?'}presser_id=${encodeURIComponent(pid)}`, opts);
    }, [consoleMode, session]);

    useEffect(() => { if (!loc && assignments.length) setLoc(assignments[0]); }, [assignments, loc]);

    const q = loc ? `shopify_location_id=${encodeURIComponent(loc.shopify_location_id)}` : '';
    const base = loc ? `/api/inv/${loc.store_key}` : '';

    const loadRuns = () => {
        if (!loc) return;
        invFetch(`${base}/runs?${q}`).then(r => r.ok ? r.json() : { runs: [] }).then(j => setRuns(j.runs || []));
    };
    useEffect(loadRuns, [loc]);   // eslint-disable-line react-hooks/exhaustive-deps

    const openRun = async (id) => {
        setMsg(null); setPlan(null); setChoices({});
        const r = await invFetch(`${base}/runs/${id}?${q}`);
        if (r.ok) setDetail(await r.json()); else setMsg((await r.json().catch(() => ({}))).detail || 'Could not open run');
    };

    const previewReverse = async () => {
        setBusy(true); setMsg(null);
        try {
            const r = await invFetch(`${base}/runs/${detail.session.id}/reverse-preview?${q}`);
            const j = await r.json().catch(() => ({}));
            if (!r.ok) { setMsg(j.detail || 'Preview failed'); return; }
            setPlan(j.plan);
        } finally { setBusy(false); }
    };

    const doReverse = async () => {
        setBusy(true); setMsg(null);
        try {
            const r = await invFetch(`${base}/runs/${detail.session.id}/reverse`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ shopify_location_id: loc.shopify_location_id, conflict_choice: choices }),
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) { setMsg(j.detail || 'Reverse failed'); return; }
            const ok = (j.results || []).filter(x => x.ok).length;
            const sk = (j.results || []).filter(x => x.skipped).length;
            setMsg(`${ok} SKU(s) restored, ${sk} skipped, ${(j.errors || []).length} failed.`
                + ((j.errors || []).length ? ' ' + j.errors.map(e => `${e.sku}: ${e.error}`).join('; ') : ''));
            setPlan(null); loadRuns(); openRun(detail.session.id);
        } finally { setBusy(false); }
    };

    const header = (
        <div style={S.bar}>
            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: 1 }}>RUN HISTORY</div>
            <div style={{ fontSize: 13, color: '#93c5fd' }}>{loc?.location_name || ''}</div>
        </div>
    );

    if (detail) {
        const { session: s, lines, run_lines: rls, breakdown } = detail;
        const rlBySku = Object.fromEntries((rls || []).map(r => [r.sku, r]));
        return (
            <div style={S.shell}>
                {header}
                <div style={S.card}>
                    <div style={{ fontWeight: 700 }}>{TYPE_LABEL[s.session_type] || s.session_type} #{s.id}</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                        Started by {s.created_by || '—'} · applied {fmt(s.applied_at)} by {s.applied_by || '—'}
                    </div>
                    {s.reversed_at && <div style={{ color: '#b91c1c', fontWeight: 700, marginTop: 6 }}>Reversed {fmt(s.reversed_at)} by {s.reversed_by}</div>}
                    {!rls?.length && <div style={{ color: '#92400e', fontSize: 13, marginTop: 6 }}>Applied before before/after was recorded: shown as counted, cannot be reversed.</div>}
                </div>
                <div style={S.card}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr>
                            <th style={S.th}>SKU</th><th style={S.th}>Counted</th><th style={S.th}>Before</th><th style={S.th}>After</th><th style={S.th}>Δ</th>
                        </tr></thead>
                        <tbody>
                            {lines.map(l => {
                                const rl = rlBySku[l.sku];
                                const bd = breakdown?.skus?.[l.sku];
                                const d = rl ? rl.after_qty - rl.before_qty : null;
                                return (
                                    <React.Fragment key={l.sku}>
                                        <tr>
                                            <td style={{ ...S.td, fontWeight: 600 }}>{l.sku}</td>
                                            <td style={S.td}>{l.scanned}</td>
                                            <td style={S.td}>{rl ? rl.before_qty : '—'}</td>
                                            <td style={S.td}>{rl ? rl.after_qty : '—'}</td>
                                            <td style={S.td}>{d === null ? '—' : d > 0 ? `+${d}` : d}{rl?.reversed_at ? ` (reversed → ${rl.reversed_to})` : ''}</td>
                                        </tr>
                                        {bd && (bd.users.length > 0 || bd.adjustment !== 0) && (
                                            <tr><td colSpan={5} style={{ ...S.td, background: '#f9fafb', fontSize: 12 }}>
                                                {bd.users.map(u => <div key={u.user}>{u.user} · {u.scanned > 0 ? `scanned ${u.scanned}` : ''}{u.scanned > 0 && u.entered !== 0 ? ' · ' : ''}{u.entered !== 0 ? `entered ${u.entered > 0 ? '+' : ''}${u.entered}` : ''}</div>)}
                                                {bd.adjustment !== 0 && <div style={{ color: '#92400e' }}>Entered by someone (not attributed) / removed scans: {bd.adjustment > 0 ? `+${bd.adjustment}` : bd.adjustment}</div>}
                                            </td></tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {msg && <div style={{ ...S.card, color: '#374151' }}>{msg}</div>}
                {plan && (
                    <div style={S.card}>
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>Reverse this run?</div>
                        {plan.map(p => (
                            <div key={p.run_line_id} style={{ fontSize: 13, padding: '4px 0' }}>
                                <b>{p.sku}</b>: on hand now {p.current ?? '?'}; run set it {p.before} → {p.after}.
                                {p.conflict ? (
                                    <span style={{ color: '#92400e' }}> Changed since the run.{' '}
                                        <select value={choices[p.sku] || 'skip'} onChange={e => setChoices(c => ({ ...c, [p.sku]: e.target.value }))}>
                                            <option value="skip">Skip</option>
                                            <option value="restore">Set to {p.before}</option>
                                            <option value="delta">Subtract the run's change ({p.delta > 0 ? `-${p.delta}` : `+${-p.delta}`}) from now</option>
                                        </select>
                                    </span>
                                ) : <span> Will be set back to {p.before}.</span>}
                            </div>
                        ))}
                        <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                            <button style={S.btn} onClick={() => setPlan(null)}>Cancel</button>
                            <button style={S.danger} disabled={busy} onClick={doReverse}>{busy ? 'Reversing…' : 'Confirm reversal'}</button>
                        </div>
                    </div>
                )}
                <div style={{ display: 'flex', gap: 10, padding: '0 12px 24px' }}>
                    <button style={S.btn} onClick={() => { setDetail(null); setPlan(null); setMsg(null); }}>← Back to history</button>
                    <button style={S.btn} onClick={() => exportReviewCsv(lines, breakdown, `run-${s.id}-report.csv`,
                        rls?.length ? Object.fromEntries(rls.map(r => [r.sku, { before: r.before_qty, after: r.after_qty }])) : null,
                        { adding: s.session_type === 'add_inventory' })}>
                        Export CSV
                    </button>
                    {isAdmin && detail.reversible && !plan && (
                        <button style={S.danger} disabled={busy} onClick={previewReverse}>Reverse this run…</button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div style={S.shell}>
            {header}
            {assignments.length > 1 && (
                <div style={{ padding: '12px 12px 0' }}>
                    <select value={loc?.shopify_location_id || ''} onChange={e => setLoc(assignments.find(a => String(a.shopify_location_id) === e.target.value))}>
                        {assignments.map(a => <option key={a.shopify_location_id} value={a.shopify_location_id}>{a.location_name}</option>)}
                    </select>
                </div>
            )}
            {msg && <div style={S.card}>{msg}</div>}
            {runs === null ? <div style={S.card}>Loading…</div>
             : runs.length === 0 ? <div style={S.card}>No applied runs yet at this location.</div>
             : runs.map(r => (
                <div key={r.id} style={{ ...S.card, cursor: 'pointer' }} onClick={() => openRun(r.id)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <b>{TYPE_LABEL[r.session_type] || r.session_type} #{r.id}</b>
                        <span style={{ fontSize: 12, color: r.reversed_at ? '#b91c1c' : '#6b7280' }}>{r.reversed_at ? 'Reversed' : fmt(r.applied_at)}</span>
                    </div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                        {r.sku_count} SKU{r.sku_count === 1 ? '' : 's'} counted
                        {r.lines_changed != null ? ` · ${r.lines_changed} changed (net ${r.net_delta > 0 ? '+' : ''}${r.net_delta})` : ' · before/after not recorded'}
                        {' · applied by '}{r.applied_by || '—'}
                    </div>
                </div>
            ))}
            <div style={{ padding: '0 12px 24px' }}>
                <button style={S.btn} onClick={() => navigate('../home', { relative: 'path' })}>← Home</button>
            </div>
        </div>
    );
}
